import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationMode } from '@pcp/shared';
import {
  dismissNotification, getNotificationPrefs, getNotifications, getUnreadNotificationCount,
  markNotificationsRead, saveNotificationOptions, saveNotificationPrefs,
  type NotificationPage, type NotificationPrefs,
} from './api';
import { useToastStore } from './stores';

/** A failed settings save rolls the control back; this says why, the same way the
 *  rest of Profile reports a failed preference save. */
function settingsSaveFailed(e: unknown) {
  useToastStore.getState().addToast({
    type: 'error',
    message: `Could not save notification settings: ${(e as Error)?.message ?? 'error'}`,
  });
}

// ─── Notification queries ────────────────────────────────────────
//
// Two queries, deliberately separate: the badge is on every page for every user,
// so it stays a single count rather than a page of rows nobody has opened. The
// SSE bridge invalidates both on a per-user `notification` event, so the poll
// below is only the safety net for a dropped stream. (f-01806)

export const NOTIFICATIONS_KEY = ['notifications'] as const;
export const UNREAD_KEY = ['notifications-unread'] as const;

/** `unread` counts events, `unreadItems` the records they are about. Every
 *  optimistic write below SPREADS the previous value rather than rebuilding the
 *  object — writing `{ unread: n }` would drop `unreadItems` and blank the badge
 *  until the refetch landed. (f-01817) */
type UnreadCounts = { unread: number; unreadItems?: number };

export function useUnreadNotifications() {
  return useQuery({
    queryKey: UNREAD_KEY,
    queryFn: getUnreadNotificationCount,
    refetchInterval: 120_000,
    staleTime: 30_000,
  });
}

export function useNotifications(params: { page?: number; category?: string; unread?: boolean; pageSize?: number }) {
  return useQuery({
    queryKey: [...NOTIFICATIONS_KEY, params.page ?? 1, params.category ?? 'all', params.unread ? 'unread' : 'all', params.pageSize ?? 100],
    queryFn: () => getNotifications(params),
    staleTime: 15_000,
  });
}

/** Mark one row read. Optimistic on BOTH the badge and any loaded page, so the
 *  dot and the row clear in the same frame the user clicks — the navigation that
 *  follows would otherwise land before the refetch and look like nothing happened. */
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => markNotificationsRead({ ids: [id] }),
    onMutate: async (id: string) => {
      const prevUnread = qc.getQueryData<UnreadCounts>(UNREAD_KEY);
      // `unreadItems` is deliberately NOT touched: one row does not tell us
      // whether its record still has other unread rows, and guessing would make
      // the badge jump the wrong way. The refetch below settles it. The tray
      // uses useMarkNotificationGroupRead instead, which does know.
      if (prevUnread) qc.setQueryData(UNREAD_KEY, { ...prevUnread, unread: Math.max(0, prevUnread.unread - 1) });
      qc.setQueriesData<NotificationPage>({ queryKey: NOTIFICATIONS_KEY }, (page) => page && ({
        ...page,
        unread: Math.max(0, page.unread - 1),
        items: page.items.map((n) => (n.id === id && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n)),
      }));
      return { prevUnread };
    },
    onError: (_e, _id, ctx) => { if (ctx?.prevUnread) qc.setQueryData(UNREAD_KEY, ctx.prevUnread); },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: UNREAD_KEY });
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}

/**
 * Mark a whole deck read in one call — the tray's path for every row it renders,
 * including a group of one.
 *
 * It has to be all-or-nothing. The tray is unread-only, so a partially-read
 * group comes back one row shorter, which reads as the tray losing things.
 * The ids are already in hand from the group, and /notifications/read has always
 * taken an array, so this needs no API change. (f-01816)
 */
export function useMarkNotificationGroupRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => markNotificationsRead({ ids }),
    onMutate: async (ids: string[]) => {
      const prevUnread = qc.getQueryData<UnreadCounts>(UNREAD_KEY);
      if (prevUnread) {
        qc.setQueryData(UNREAD_KEY, {
          ...prevUnread,
          // N events leave, but only ONE record does — that is the whole point
          // of the two counts.
          unread: Math.max(0, prevUnread.unread - ids.length),
          ...(prevUnread.unreadItems == null ? {} : { unreadItems: Math.max(0, prevUnread.unreadItems - 1) }),
        });
      }
      const seen = new Set(ids);
      qc.setQueriesData<NotificationPage>({ queryKey: NOTIFICATIONS_KEY }, (page) => page && ({
        ...page,
        unread: Math.max(0, page.unread - ids.length),
        items: page.items.map((n) => (seen.has(n.id) && !n.readAt ? { ...n, readAt: new Date().toISOString() } : n)),
      }));
      return { prevUnread };
    },
    onError: (_e, _ids, ctx) => { if (ctx?.prevUnread) qc.setQueryData(UNREAD_KEY, ctx.prevUnread); },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: UNREAD_KEY });
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => markNotificationsRead({ all: true }),
    onMutate: async () => {
      const prevUnread = qc.getQueryData<UnreadCounts>(UNREAD_KEY);
      qc.setQueryData(UNREAD_KEY, { unread: 0, unreadItems: 0 });
      qc.setQueriesData<NotificationPage>({ queryKey: NOTIFICATIONS_KEY }, (page) => page && ({
        ...page,
        unread: 0,
        items: page.items.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() })),
      }));
      return { prevUnread };
    },
    onError: (_e, _v, ctx) => { if (ctx?.prevUnread) qc.setQueryData(UNREAD_KEY, ctx.prevUnread); },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: UNREAD_KEY });
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}

export function useDismissNotification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dismissNotification(id),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: UNREAD_KEY });
      void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    },
  });
}

export const NOTIFICATION_PREFS_KEY = ['notification-prefs'] as const;

export function useNotificationPrefs() {
  return useQuery({ queryKey: NOTIFICATION_PREFS_KEY, queryFn: getNotificationPrefs, staleTime: 60_000 });
}

/** Save on toggle, like the rest of Profile. Optimistic so the segment moves
 *  under the finger rather than after a round trip. */
export function useSaveNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (modes: Record<string, NotificationMode>) => saveNotificationPrefs(modes),
    onMutate: async (modes) => {
      const prev = qc.getQueryData<{ kinds: unknown[]; modes: Record<string, NotificationMode> }>(NOTIFICATION_PREFS_KEY);
      if (prev) qc.setQueryData(NOTIFICATION_PREFS_KEY, { ...prev, modes: { ...prev.modes, ...modes } });
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(NOTIFICATION_PREFS_KEY, ctx.prev);
      settingsSaveFailed(e);
    },
    onSettled: () => { void qc.invalidateQueries({ queryKey: NOTIFICATION_PREFS_KEY }); },
  });
}

/** Switch a notification option ("Include child items of my Features"), the same
 *  way: optimistic, rolled back on failure, then refetched. Spreads the previous
 *  value so `kinds`, `modes` and `optionDefs` survive the optimistic write. (f-01956) */
export function useSaveNotificationOptions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (options: Record<string, boolean>) => saveNotificationOptions(options),
    onMutate: async (options) => {
      await qc.cancelQueries({ queryKey: NOTIFICATION_PREFS_KEY });
      const prev = qc.getQueryData<NotificationPrefs>(NOTIFICATION_PREFS_KEY);
      if (prev) qc.setQueryData<NotificationPrefs>(NOTIFICATION_PREFS_KEY, { ...prev, options: { ...prev.options, ...options } });
      return { prev };
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(NOTIFICATION_PREFS_KEY, ctx.prev);
      settingsSaveFailed(e);
    },
    onSettled: () => { void qc.invalidateQueries({ queryKey: NOTIFICATION_PREFS_KEY }); },
  });
}
